import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  apiAdminCreatePlan,
  apiAdminListPlans,
  apiAdminUpdatePlan,
  apiAdminUpdatePlanStatus,
  ApiErrorResponse,
  SubscriptionPlan,
} from '../api/client';
import PortalPageShell from './PortalPageShell';

type PlanFormState = {
  name: string;
  description: string;
  amount: string;
  currency: string;
  billing_cycle: string;
  duration_days: string;
  is_active: boolean;
};

const defaultFormState: PlanFormState = {
  name: '',
  description: '',
  amount: '',
  currency: 'AUD',
  billing_cycle: 'monthly',
  duration_days: '30',
  is_active: true,
};

function isPlanActive(plan: SubscriptionPlan) {
  return plan.is_active === true || plan.is_active === 1;
}

function toFormState(plan?: SubscriptionPlan | null): PlanFormState {
  if (!plan) {
    return defaultFormState;
  }

  return {
    name: plan.name || '',
    description: plan.description || '',
    amount: String(plan.amount ?? ''),
    currency: plan.currency || 'AUD',
    billing_cycle: plan.billing_cycle || 'monthly',
    duration_days: String(plan.duration_days ?? ''),
    is_active: isPlanActive(plan),
  };
}

export default function AdminPlans() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingPlanId, setUpdatingPlanId] = useState<number | null>(null);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [formState, setFormState] = useState<PlanFormState>(defaultFormState);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    const data: SubscriptionPlan[] | ApiErrorResponse = await apiAdminListPlans();

    if (Array.isArray(data)) {
      const sortedPlans = [...data].sort((left, right) => {
        const leftSort = left.sort_order ?? 0;
        const rightSort = right.sort_order ?? 0;

        if (leftSort !== rightSort) {
          return leftSort - rightSort;
        }

        return left.id - right.id;
      });

      setPlans(sortedPlans);
      setError('');
    } else {
      setPlans([]);
      setError(data?.message || 'Unable to load subscription plans right now.');
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const formatDate = (value?: string | null) => {
    if (!value) return '-';

    const date = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const amountPreview = useMemo(() => {
    if (!formState.amount) {
      return '-';
    }

    const amount = Number(formState.amount);
    if (Number.isNaN(amount)) {
      return formState.amount;
    }

    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: formState.currency || 'AUD',
    }).format(amount);
  }, [formState.amount, formState.currency]);

  const openCreateModal = () => {
    setEditingPlan(null);
    setFormState(defaultFormState);
    onOpen();
  };

  const openEditModal = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormState(toFormState(plan));
    onOpen();
  };

  const closeModal = () => {
    if (saving) return;
    onClose();
    setEditingPlan(null);
    setFormState(defaultFormState);
  };

  const handleFormChange = (field: keyof PlanFormState, value: string | boolean) => {
    setFormState(current => ({
      ...current,
      [field]: value,
    }));
  };

  const validateForm = () => {
    if (!formState.name.trim()) {
      return 'Plan name is required.';
    }

    if (!formState.amount.trim() || Number.isNaN(Number(formState.amount))) {
      return 'A valid amount is required.';
    }

    if (!formState.billing_cycle.trim()) {
      return 'Billing cycle is required.';
    }

    if (!formState.duration_days.trim() || Number.isNaN(Number(formState.duration_days))) {
      return 'Duration in days is required.';
    }

    return '';
  };

  const handleSave = async () => {
    const validationMessage = validateForm();

    if (validationMessage) {
      toast({
        title: 'Validation error',
        description: validationMessage,
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    const payload = {
      name: formState.name.trim(),
      description: formState.description.trim() || undefined,
      amount: Number(formState.amount),
      currency: formState.currency.trim() || 'AUD',
      billing_cycle: formState.billing_cycle,
      duration_days: Number(formState.duration_days),
      sort_order: editingPlan?.sort_order ?? 0,
      is_active: formState.is_active,
    };

    try {
      setSaving(true);

      const result: any = editingPlan
        ? await apiAdminUpdatePlan(editingPlan.id, payload)
        : await apiAdminCreatePlan(payload);

      if (result?.success) {
        toast({
          title: editingPlan ? 'Plan updated' : 'Plan created',
          description: result?.message || 'Plan saved successfully.',
          status: 'success',
          duration: 3500,
          isClosable: true,
        });
        closeModal();
        loadPlans();
        return;
      }

      toast({
        title: 'Save failed',
        description: result?.message || 'Unable to save the plan right now.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (plan: SubscriptionPlan) => {
    const nextIsActive = !isPlanActive(plan);
    setUpdatingPlanId(plan.id);

    try {
      const result: any = await apiAdminUpdatePlanStatus(plan.id, nextIsActive);

      if (result?.success) {
        setPlans(currentPlans =>
          currentPlans.map(item =>
            item.id === plan.id
              ? {
                  ...item,
                  ...(result.plan || {}),
                  is_active: nextIsActive,
                }
              : item
          )
        );

        toast({
          title: nextIsActive ? 'Plan enabled' : 'Plan disabled',
          description: result?.message || 'Plan status updated successfully.',
          status: 'success',
          duration: 3500,
          isClosable: true,
        });
        return;
      }

      toast({
        title: 'Status update failed',
        description: result?.message || 'Unable to update the plan status right now.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUpdatingPlanId(null);
    }
  };

  if (loading) {
    return (
      <PortalPageShell title="Subscriptions" subtitle="Manage subscription plans for the portal.">
        <Stack align="center" py={12} spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.600">Loading subscription plans...</Text>
        </Stack>
      </PortalPageShell>
    );
  }

  return (
    <>
      <PortalPageShell
        title="Subscriptions"
        subtitle="Create, edit, and enable or disable subscription plans."
        actions={
          <Button colorScheme="blue" onClick={openCreateModal}>
            Create Plan
          </Button>
        }
      >
        <Stack spacing={5}>
          {error ? (
            <Alert status="warning" borderRadius="lg">
              <AlertIcon />
              {error}
            </Alert>
          ) : null}

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
            {plans.map(plan => {
              const active = isPlanActive(plan);

              return (
                <Stack key={plan.id} p={5} borderWidth="1px" borderColor="gray.200" borderRadius="xl" spacing={4}>
                  <HStack justify="space-between" align="flex-start" flexWrap="wrap">
                    <Stack spacing={1}>
                      <Text fontWeight="700" fontSize="lg" color="gray.800">
                        {plan.name}
                      </Text>
                      <Text color="gray.600">{plan.billing_summary || plan.description || 'No plan summary available.'}</Text>
                    </Stack>
                    <HStack spacing={2} flexWrap="wrap">
                      <Badge colorScheme={active ? 'green' : 'red'}>{active ? 'Active' : 'Disabled'}</Badge>
                      <Badge colorScheme="blue">{plan.billing_cycle}</Badge>
                    </HStack>
                  </HStack>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <Text><strong>Amount:</strong> {plan.currency || 'AUD'} {Number(plan.amount).toFixed(2)}</Text>
                    <Text><strong>Duration:</strong> {plan.duration_days} days</Text>
                    <Text><strong>Updated:</strong> {formatDate(plan.updated_at || plan.created_at)}</Text>
                  </SimpleGrid>

                  {plan.description ? <Text color="gray.600">{plan.description}</Text> : null}

                  <HStack spacing={3} pt={2} flexWrap="wrap">
                    <Button size="sm" colorScheme="blue" variant="outline" onClick={() => openEditModal(plan)}>
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      colorScheme={active ? 'red' : 'green'}
                      variant="outline"
                      onClick={() => handleToggleStatus(plan)}
                      isLoading={updatingPlanId === plan.id}
                      loadingText={active ? 'Disabling' : 'Enabling'}
                      isDisabled={updatingPlanId !== null && updatingPlanId !== plan.id}
                    >
                      {active ? 'Disable' : 'Enable'}
                    </Button>
                  </HStack>
                </Stack>
              );
            })}
          </SimpleGrid>

          {!error && plans.length === 0 ? <Text color="gray.600">No subscription plans have been created yet.</Text> : null}
        </Stack>
      </PortalPageShell>

      <Modal isOpen={isOpen} onClose={closeModal} size="2xl" isCentered>
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent>
          <ModalHeader>{editingPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}</ModalHeader>
          <ModalCloseButton isDisabled={saving} />
          <ModalBody>
            <Stack spacing={5}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Plan Name</FormLabel>
                  <Input
                    value={formState.name}
                    onChange={e => handleFormChange('name', e.target.value)}
                    placeholder="Plan name"
                    isDisabled={saving}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Amount</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={formState.amount}
                    onChange={e => handleFormChange('amount', e.target.value)}
                    placeholder="1.99"
                    isDisabled={saving}
                  />
                  <FormHelperText>Preview: {amountPreview}</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>Currency</FormLabel>
                  <Input
                    value={formState.currency}
                    onChange={e => handleFormChange('currency', e.target.value.toUpperCase())}
                    placeholder="AUD"
                    isDisabled={saving}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Billing Cycle</FormLabel>
                  <Select
                    value={formState.billing_cycle}
                    onChange={e => handleFormChange('billing_cycle', e.target.value)}
                    isDisabled={saving}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Duration Days</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    value={formState.duration_days}
                    onChange={e => handleFormChange('duration_days', e.target.value)}
                    placeholder="30"
                    isDisabled={saving}
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center" pt={8}>
                  <Switch
                    isChecked={formState.is_active}
                    onChange={e => handleFormChange('is_active', e.target.checked)}
                    isDisabled={saving}
                    colorScheme="green"
                    mr={3}
                  />
                  <FormLabel mb="0">Plan is active</FormLabel>
                </FormControl>
              </SimpleGrid>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={formState.description}
                  onChange={e => handleFormChange('description', e.target.value)}
                  placeholder="Customer-facing plan description"
                  rows={4}
                  isDisabled={saving}
                />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={closeModal} isDisabled={saving}>
                Cancel
              </Button>
              <Button colorScheme="blue" onClick={handleSave} isLoading={saving} loadingText={editingPlan ? 'Saving' : 'Creating'}>
                {editingPlan ? 'Save Changes' : 'Create Plan'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}